import { get } from 'lodash/fp';
import moment from 'moment';
import { dateFormatZurich } from './datetime';

export const SORT_DIRECTION_ASC = 'asc';
export const SORT_DIRECTION_DESC = 'desc';

export type SortDirection = typeof SORT_DIRECTION_ASC | typeof SORT_DIRECTION_DESC;

type AnyObject = Record<string, unknown>;

interface SortOptions {
    direction: SortDirection;
}

// ─── Date ────────────────────────────────────────────────────────────────────

export const sorterByDateCommon = (column: string) => (previous: AnyObject, current: AnyObject): number => {
    const previousValue = get(column, previous) as string | undefined;
    const currentValue = get(column, current) as string | undefined;

    if (previousValue === undefined || currentValue === undefined) return 0;

    return new Date(previousValue).getTime() - new Date(currentValue).getTime();
};

export const sorterByDate = (column: string) => (...props: [AnyObject[], SortOptions] | [AnyObject, AnyObject, unknown]): AnyObject[] | number => {
    if (props.length === 3) return sorterByDateCommon(column)(props[0] as AnyObject, props[1] as AnyObject);

    const [data, { direction }] = props as [AnyObject[], SortOptions];

    return data.sort((previous, current) => {
        const previousValue = get(column, previous) as string | undefined;
        const currentValue = get(column, current) as string | undefined;

        if (previousValue === undefined || currentValue === undefined) return 0;

        if (direction === SORT_DIRECTION_ASC) return new Date(previousValue).getTime() - new Date(currentValue).getTime();
        if (direction === SORT_DIRECTION_DESC) return new Date(currentValue).getTime() - new Date(previousValue).getTime();

        return 0;
    });
};

// ─── String ──────────────────────────────────────────────────────────────────

export const sortStrings = (
    previous: string | undefined,
    current: string | undefined,
    direction: SortDirection = SORT_DIRECTION_ASC,
): number => {
    if (previous === undefined || current === undefined) return 0;

    const prev = typeof previous === 'string' ? previous.toLowerCase() : previous;
    const curr = typeof current === 'string' ? current.toLowerCase() : current;

    if (direction === SORT_DIRECTION_ASC) return prev.localeCompare(curr);
    if (direction === SORT_DIRECTION_DESC) return curr.localeCompare(prev);

    return 0;
};

export const sorterStringCommon = (column: string) => (previous: AnyObject, current: AnyObject): number => {
    const previousValue = get(column, previous) as string | undefined;
    const currentValue = get(column, current) as string | undefined;

    if (previousValue === undefined || currentValue === undefined) return 0;

    const prev = typeof previousValue === 'string' ? previousValue.toLowerCase() : previousValue;
    const curr = typeof currentValue === 'string' ? currentValue.toLowerCase() : currentValue;

    return prev.localeCompare(curr);
};

interface LabelValueObject {
    label?: string;
    value?: string;
    [key: string]: unknown;
}

const sortObjectString = (key: keyof LabelValueObject, obj: LabelValueObject): string | undefined => (Object.prototype.hasOwnProperty.call(obj, key) ? obj[key]?.toLowerCase() : undefined);

export const sorterString = (column: string) => (...props: [AnyObject[], SortOptions] | [AnyObject, AnyObject, unknown]): AnyObject[] | number => {
    if (props.length === 3) return sorterStringCommon(column)(props[0] as AnyObject, props[1] as AnyObject);

    const [data, { direction }] = props as [AnyObject[], SortOptions];

    return data.sort((previous, current) => {
        let previousValue = get(column, previous) as string | LabelValueObject | undefined;
        let currentValue = get(column, current) as string | LabelValueObject | undefined;

        if (typeof previousValue === 'string') previousValue = previousValue.toLowerCase();
        if (typeof currentValue === 'string') currentValue = currentValue.toLowerCase();

        if (typeof previousValue === 'object' && previousValue !== null) {
            previousValue = sortObjectString('label', previousValue) || sortObjectString('value', previousValue);
        }

        if (typeof currentValue === 'object' && currentValue !== null) {
            currentValue = sortObjectString('label', currentValue) || sortObjectString('value', currentValue);
        }

        if (previousValue === undefined || currentValue === undefined) return 0;

        if (direction === SORT_DIRECTION_ASC) return (previousValue as string).localeCompare(currentValue as string);
        if (direction === SORT_DIRECTION_DESC) return (currentValue as string).localeCompare(previousValue as string);

        return 0;
    });
};

// ─── Number ──────────────────────────────────────────────────────────────────

export const sorterNumberCommon = (column: string) => (previous: AnyObject, current: AnyObject): number => {
    let previousValue = get(column, previous) as number | string;
    let currentValue = get(column, current) as number | string;

    if (typeof previousValue === 'string') previousValue = Number(previousValue.replace(/[^\d.-]/g, ''));
    if (typeof currentValue === 'string') currentValue = Number(currentValue.replace(/[^\d.-]/g, ''));

    return (previousValue as number) - (currentValue as number);
};

export const sorterNumber = (column: string) => (...props: [AnyObject[], SortOptions] | [AnyObject, AnyObject, unknown]): AnyObject[] | number => {
    if (props.length === 3) return sorterNumberCommon(column)(props[0] as AnyObject, props[1] as AnyObject);

    const [data, { direction }] = props as [AnyObject[], SortOptions];

    return data.sort((previous, current) => {
        let previousValue = get(column, previous) as number | string | undefined;
        let currentValue = get(column, current) as number | string | undefined;

        if (typeof previousValue === 'string') previousValue = Number(previousValue.replace(/[^\d.-]/g, ''));
        if (typeof currentValue === 'string') currentValue = Number(currentValue.replace(/[^\d.-]/g, ''));

        if (previousValue === undefined || currentValue === undefined) return 0;

        if (direction === SORT_DIRECTION_ASC) return (previousValue as number) - (currentValue as number);
        if (direction === SORT_DIRECTION_DESC) return (currentValue as number) - (previousValue as number);

        return 0;
    });
};

// ─── Status Color ────────────────────────────────────────────────────────────

type StatusColor = 'green' | 'red' | 'yellow' | 'black';

interface StatusColorValue {
    color: StatusColor | string;
    text: string;
}

const colorOrder: Record<StatusColor, number> = {
    green: 1,
    red: 2,
    yellow: 3,
    black: 4,
};

export const sorterStatusColor = (column: string) => (...props: [AnyObject[], SortOptions]): AnyObject[] => {
    const [data, { direction }] = props;

    return data.sort((previous, current) => {
        const previousValue = get(column, previous) as StatusColorValue | undefined;
        const currentValue = get(column, current) as StatusColorValue | undefined;

        if (previousValue === undefined || currentValue === undefined) return 0;

        if (previousValue.color === currentValue.color) {
            return sortStrings(previousValue.text, currentValue.text, direction);
        }

        const previousColor = typeof previousValue.color === 'string'
            ? previousValue.color.toLowerCase() as StatusColor
            : previousValue.color;

        const currentColor = typeof currentValue.color === 'string'
            ? currentValue.color.toLowerCase() as StatusColor
            : currentValue.color;

        if (direction === SORT_DIRECTION_ASC) return colorOrder[previousColor] - colorOrder[currentColor];
        if (direction === SORT_DIRECTION_DESC) return colorOrder[currentColor] - colorOrder[previousColor];

        return 0;
    });
};

// ─── String Date ─────────────────────────────────────────────────────────────

export const sorterByStringDateCommon = (column: string) => (previous: AnyObject, current: AnyObject): number => {
    const previousValue = moment(get(column, previous) as string, dateFormatZurich);
    const currentValue = moment(get(column, current) as string, dateFormatZurich);

    if (!previousValue.isValid() || !currentValue.isValid()) return 0;

    return previousValue.valueOf() - currentValue.valueOf();
};

export const sorterByStringDate = (column: string) => (...props: [AnyObject[], SortOptions] | [AnyObject, AnyObject, unknown]): AnyObject[] | number => {
    if (props.length === 3) return sorterByStringDateCommon(column)(props[0] as AnyObject, props[1] as AnyObject);

    const [data, { direction }] = props as [AnyObject[], SortOptions];

    return data.sort((previous, current) => {
        const previousValue = moment(get(column, previous) as string, dateFormatZurich);
        const currentValue = moment(get(column, current) as string, dateFormatZurich);

        if (!previousValue.isValid() || !currentValue.isValid()) return 0;

        if (direction === SORT_DIRECTION_ASC) return previousValue.valueOf() - currentValue.valueOf();
        if (direction === SORT_DIRECTION_DESC) return currentValue.valueOf() - previousValue.valueOf();

        return 0;
    });
};
